// PBX Host detail — the binding view for the three systems a call traverses.
//
// A call to a DID only completes when Commio (carrier routing), GDMS (device
// registration target), and FreePBX (the terminating PBX) all reference the
// SAME physical host. These are pinned per-host as commio_route_id /
// gdms_sip_server_id / FreePBX creds, but the labels alone don't tell an
// operator they line up (Commio "LyonsCommPBX" vs GDMS "pbx-02.lyonscomm.com").
// This page resolves each pin to its underlying address and flags mismatch,
// plus surfaces DID-routing drift and a confirmed bulk re-route.

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { canAdmin } from '../lib/roles.js';
import PbxHostInterfaces from '../components/agents/PbxHostInterfaces.jsx';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import PbxHostEditModal from '../components/agents/PbxHostEditModal.jsx';
import PbxHostDiagnostics from '../components/agents/PbxHostDiagnostics.jsx';

function Card({ title, children, verdict }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {verdict}
      </div>
      <div className="mt-2 space-y-1 text-sm text-gray-600">{children}</div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-gray-400">{label}</span>
      <span className={`text-right ${mono ? 'font-mono text-xs' : ''} text-gray-800`}>
        {value || <span className="text-gray-300">—</span>}
      </span>
    </div>
  );
}

export default function PbxHostDetail() {
  const { agentId } = useParams();
  const { role } = useAuth();
  const isAdmin = canAdmin(role);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [rerouteResult, setRerouteResult] = useState(null);
  // TWO TABS, because the page answers two different questions and only one of them is about routing.
  // Kevin, 2026-08-20: "add to the pbx host detail page, a diagnostics tab." Everything that was here
  // is the binding view — do Commio, GDMS and FreePBX name the same box — and it is unchanged.
  const [tab, setTab] = useState('routing');

  const { data: hosts, isLoading: hostsLoading } = useQuery({
    queryKey: ['pbx-hosts'],
    queryFn: () => api.get('/pbx-hosts'),
  });
  const host = (Array.isArray(hosts) ? hosts : []).find(
    (h) => (h.agent_id || h.id) === agentId,
  );

  // `isFetching` as well as `isLoading`, and `dataUpdatedAt`, both for the Re-check button below.
  // `isLoading` is only ever true on the FIRST load, so a refetch had no visible effect anywhere.
  const {
    data: verify, isLoading: verifyLoading, isFetching: verifyFetching,
    dataUpdatedAt: verifyAt, refetch: refetchVerify,
  } = useQuery({
    queryKey: ['pbx-host-verify', agentId],
    queryFn: () => api.get(`/pbx-hosts/${agentId}/routing-verify`),
    retry: false,
  });

  const editMutation = useMutation({
    mutationFn: (body) => api.put(`/agents/${agentId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pbx-hosts'] });
      queryClient.invalidateQueries({ queryKey: ['pbx-host-verify', agentId] });
      setEditing(false);
    },
  });

  // The carrier half of "a call can reach this PBX". Kevin, 2026-09-04: "you should also be able
  // to create the Commio incoming route." Separate from the GDMS create because the two systems
  // fail independently and one succeeding is worth keeping.
  const commioRouteMutation = useMutation({
    mutationFn: () => api.post(`/pbx-hosts/${agentId}/commio-route`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pbx-hosts'] });
      queryClient.invalidateQueries({ queryKey: ['pbx-host-verify', agentId] });
      refetchVerify();
    },
  });

  // THE OTHER DIRECTION. The route above is inbound — where the carrier delivers calls FOR this
  // host. This is outbound: an IP allowlist entry saying Commio may accept calls FROM it. A host
  // with one and not the other takes calls but cannot make them, which looks like a PBX fault.
  //
  // Kevin, 2026-09-05: "you should be able to add the Commio trunk. match pbx-01/02."
  const { data: trunkInfo } = useQuery({
    queryKey: ['pbx-host-commio-trunk', agentId],
    queryFn: () => api.get(`/pbx-hosts/${agentId}/commio-trunk`),
    enabled: !!agentId,
  });
  const [trunkAddress, setTrunkAddress] = useState('');
  const commioTrunkMutation = useMutation({
    mutationFn: (address) => api.post(`/pbx-hosts/${agentId}/commio-trunk`, { address }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pbx-host-commio-trunk', agentId] });
      setTrunkAddress('');
    },
  });

  // WHAT THE HOST ITSELF SAYS. New in agent v0.61.2 (`trunk-read`) and it replaces an assertion:
  // before this, "pbx-04 carries no service because it has no trunk" was inferred from the outside,
  // because the agent could count trunk registrations without being able to say what the trunks
  // were. `can_create` comes back alongside so this does not fetch capabilities twice.
  const { data: hostTrunks } = useQuery({
    queryKey: ['pbx-host-trunks', agentId],
    queryFn: () => api.get(`/pbx-hosts/${agentId}/trunks`),
    enabled: !!agentId,
    // A 409 here is a real answer — an agent too old to know the slug — not a transient failure.
    retry: false,
  });
  // ADDING A SECOND TRUNK WAS NOT POSSIBLE FROM HERE.
  //
  // The button below only appeared on a host with ZERO trunks and hard-coded
  // `{name: 'Commio', host: 'icr.commio.com'}` — a first-trunk bootstrap, not trunk management. So
  // the moment a host had one, the only way to add another was the API, which is how pbx-04 spent
  // 2026-09-12 unable to receive a call: a carrier signals inbound from addresses that are not the
  // one you dial out to, and every other host in the fleet carries separate trunks for them.
  const [newTrunk, setNewTrunk] = useState({ name: '', host: '' });

  const hostTrunkMutation = useMutation({
    mutationFn: (b) => api.post(`/pbx-hosts/${agentId}/trunks`, b),
    onSuccess: () => {
      setNewTrunk({ name: '', host: '' });
      queryClient.invalidateQueries({ queryKey: ['pbx-host-trunks', agentId] });
    },
  });

  const rerouteMutation = useMutation({
    mutationFn: () => api.post(`/pbx-hosts/${agentId}/reroute-dids`, { only_drifted: true }),
    onSuccess: (res) => {
      setRerouteResult(res);
      queryClient.invalidateQueries({ queryKey: ['pbx-host-verify', agentId] });
      refetchVerify();
    },
  });

  if (hostsLoading) return <LoadingSpinner message="Loading PBX host…" />;
  if (!host) {
    return (
      <div className="space-y-4">
        <Link to="/settings?tab=pbx-hosts" className="text-sm text-blue-600 hover:underline">← PBX Hosts</Link>
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          PBX host <span className="font-mono">{agentId}</span> not found.
        </div>
      </div>
    );
  }

  const drift = verify?.did_drift;
  const offRoute = drift?.off_route || [];
  const match = verify?.match;
  // THE VERDICT IS ALL THREE LINKS, not the first two. `pbx_answers_for_address` is null where the
  // backend has no domain to report (every FreePBX host), and a null must not drag a good verdict
  // down — only a definite `false` is a mismatch.
  const routingBad = match?.commio_vs_gdms === false || match?.pbx_answers_for_address === false;
  const routingGood = !routingBad && match?.commio_vs_gdms === true;
  const matchVerdict =
    match?.commio_vs_gdms === true ? <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">addresses match</span>
    : match?.commio_vs_gdms === false ? <span className="rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">mismatch</span>
    : <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">unverified</span>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/settings?tab=pbx-hosts" className="text-sm text-blue-600 hover:underline">← PBX Hosts</Link>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">{host.name}</h1>
          <p className="font-mono text-xs text-gray-400">{agentId}</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={host.status === 'active' ? 'active' : 'unknown'} label={host.status} />
          {isAdmin && (
            <button
              onClick={() => { setEditing(true); editMutation.reset(); }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Edit assignments
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-gray-200" role="tablist">
        {[['routing', 'Routing'], ['diagnostics', 'Diagnostics']].map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px rounded-t-md border-b-2 px-3 py-2 text-sm ${
              tab === id
                ? 'border-blue-600 font-semibold text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'diagnostics' && (
        <PbxHostDiagnostics agentId={agentId} hostName={host.name} />
      )}

      {tab === 'routing' && (<>
      {/* Cross-system routing verdict banner.

          IT SAID "COMMIO, GDMS AND THE PBX LINE UP" WHILE CHECKING TWO OF THE THREE. The PBX's own
          half was never read, so pbx-04 showed this in green for a week with a tenant domain of
          `pbx-04.mgmt.lan` — no device could register and no inbound call could land, and the two
          links it did check were both genuinely correct. A green banner over a host that cannot
          take a call is worse than no banner.

          `pbx_answers_for_address` is null on a host that does not serve a tenant domain, which is
          every FreePBX host and is not a fault — so the verdict stays "Commio and GDMS agree" there
          rather than claiming a third check it did not make. */}
      <div className={`rounded-lg border px-4 py-3 text-sm ${
        routingBad ? 'border-red-200 bg-red-50 text-red-800'
        : routingGood ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-gray-200 bg-gray-50 text-gray-700'
      }`}>
        <div className="flex items-center justify-between">
          <span className="font-medium">
            {verifyLoading ? 'Verifying routing…'
              : routingBad
                ? (match?.pbx_answers_for_address === false
                  ? `Routing mismatch — ${host.name} does not answer for the address calls and phones are sent to.`
                  : 'Routing mismatch — calls will not complete.')
              : routingGood
                ? (match?.pbx_answers_for_address === true
                  ? 'Routing verified — Commio, GDMS and the PBX all agree on the address.'
                  : 'Commio and GDMS agree. This PBX does not report a domain of its own, which is normal.')
                : 'Routing not fully verified.'}
          </span>
          {/* THIS BUTTON WORKED AND LOOKED BROKEN, which is the same thing to the person clicking
              it. Kevin, 2026-09-04: "The 're-check' link does nothing… its not clickable for one…
              although it is underlined."

              Three separate reasons it read as dead, none of them the click handler — a real click
              did fire the refetch and did reach the API:
                * no `cursor-pointer`, so hovering it gave an arrow, not a hand. Underlined text
                  with an arrow cursor reads as disabled.
                * `verifyLoading` is react-query's `isLoading`, true only on the FIRST load, so a
                  refetch showed no spinner and changed no text.
                * on a host with nothing assigned the response is byte-identical every time, so
                  even a successful re-check moved nothing on screen.

              Feedback has to come from the act, not from the answer changing — the common case
              here is an answer that SHOULD be identical. */}
          <div className="flex items-center gap-2">
            {verifyAt > 0 && !verifyFetching && (
              <span className="text-[11px] opacity-70">
                checked {new Date(verifyAt).toLocaleTimeString()}
              </span>
            )}
            <button
              type="button"
              onClick={() => refetchVerify()}
              disabled={verifyFetching}
              className="cursor-pointer text-xs underline hover:no-underline disabled:cursor-wait disabled:no-underline disabled:opacity-70"
            >
              {verifyFetching ? 'Checking…' : 'Re-check'}
            </button>
          </div>
        </div>
        {(match?.notes || []).length > 0 && (
          <ul className="mt-2 list-disc pl-5 text-xs">
            {match.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        )}
      </div>

      {/* THE CARRIER HAS NOWHERE TO DELIVER THIS HOST'S NUMBERS, and this is the control for it.
          The address is NOT asked for again: routing-verify's whole verdict is whether the Commio
          routeToAddress and the GDMS sipServer are the same host, so the profile is created at the
          address already recorded here. Both live hosts follow that exactly — PBX-02's profile is
          named pbx-02.lyonscomm.com, delivers to pbx-02.lyonscomm.com, and its GDMS record is the
          same string. A profile at any other address IS the mismatch this banner reports. */}
      {isAdmin && verify && verify.commio?.pinned_route_id == null && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-medium text-amber-900">
            No Commio route for this host — the carrier has nowhere to deliver its numbers.
          </p>
          {verify.gdms?.sip_server ? (
            <>
              <p className="mt-1 text-xs text-amber-800">
                Verso will create a static inbound profile delivering to{' '}
                <span className="font-mono">{verify.gdms.sip_server}</span> — the same address GDMS
                already uses for this host, which is what makes the two line up.
              </p>
              <button
                type="button"
                onClick={() => commioRouteMutation.mutate()}
                disabled={commioRouteMutation.isPending}
                className="mt-2 cursor-pointer rounded-md bg-amber-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800 disabled:cursor-wait disabled:opacity-70"
              >
                {commioRouteMutation.isPending ? 'Creating in Commio…' : 'Create the Commio route'}
              </button>
            </>
          ) : (
            // ORDER MATTERS: without a GDMS address there is nothing to point the carrier at, and
            // guessing one sends every inbound call for this host into a hole.
            <p className="mt-1 text-xs text-amber-800">
              Create this host&apos;s GDMS SIP Server first — the carrier profile has to deliver to
              the same address, and there is not one recorded yet.
            </p>
          )}
          {commioRouteMutation.isError && (
            <p className="mt-2 text-sm text-red-700">
              {commioRouteMutation.error.body?.error || commioRouteMutation.error.message}
            </p>
          )}
          {commioRouteMutation.data?.warning && (
            <p className="mt-2 text-sm text-amber-900">{commioRouteMutation.data.warning}</p>
          )}
        </div>
      )}

      {/* OUTBOUND. Deliberately not phrased as a verdict: Commio keys a trunk on the public IP the
          PBX sends from, and Verso stores hostnames, so "this host has no trunk" is not a fact we
          hold. The name match is shown for what it is — a label, not a binding — and the address
          is typed by the operator because they are the only one who knows it. */}
      {/* Either half is worth showing on its own: the PBX trunk is what makes a call possible at
          all, and it is readable whether or not this org has Commio credentials on file. */}
      {isAdmin && (trunkInfo?.configured || hostTrunks) && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">Carrier trunk</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Outbound calling needs both halves.
          </p>

          {/* THE PBX HALF. On an Aria host this is what `CreateDidRequest.trunk_id` needs — without
              it the host can hold extensions and inbound routes and still carry no service at all.
              Create is Aria-only by the agent's decision, not ours: on FreePBX a trunk is ~64
              `pjsip` keywords of which `context` and `identify_by` decide whether an inbound call
              is trusted, so it reads as not applicable rather than as something to fix here. */}
          <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="text-xs font-medium text-gray-700">On the PBX</p>
            {hostTrunks?.trunks?.length ? (
              <>
                <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
                  {hostTrunks.trunks.map((t) => (
                    <li key={t.id}>
                      <span className="font-mono">{t.name}</span> → <span className="font-mono">{t.host}</span>
                      {t.transport ? <> · {t.transport}</> : null}
                      {t.enabled === false ? <span className="text-amber-700"> · disabled</span> : null}
                    </li>
                  ))}
                </ul>
                {hostTrunks.can_create && (
                  <form
                    className="mt-3 border-t border-gray-100 pt-3"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = newTrunk.name.trim();
                      const host = newTrunk.host.trim();
                      if (name && host) hostTrunkMutation.mutate({ name, host });
                    }}
                  >
                    <p className="text-xs font-medium text-gray-700">Add a trunk</p>
                    {/* SAID HERE BECAUSE IT IS THE PART THAT IS NOT OBVIOUS. The trunk you dial OUT
                        to and the addresses a carrier signals IN from are different things, and a
                        host missing the inbound ones takes no calls while looking entirely healthy
                        — pbx-04, 2026-09-12. */}
                    <p className="mt-0.5 text-[11px] leading-tight text-gray-500">
                      One per address. A carrier usually signals inbound calls from addresses other
                      than the one you dial out to — this PBX will refuse a call from an address it
                      has no trunk for.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <input
                        value={newTrunk.name}
                        onChange={(e) => setNewTrunk((v) => ({ ...v, name: e.target.value }))}
                        placeholder="Name, e.g. CommioInbound-192.81.236.20"
                        className="min-w-[14rem] flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
                      />
                      <input
                        value={newTrunk.host}
                        onChange={(e) => setNewTrunk((v) => ({ ...v, host: e.target.value }))}
                        placeholder="Address, e.g. 192.81.236.20"
                        className="min-w-[10rem] flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
                      />
                      <button
                        type="submit"
                        disabled={hostTrunkMutation.isPending || !newTrunk.name.trim() || !newTrunk.host.trim()}
                        className="cursor-pointer rounded-md bg-gray-800 px-3 py-1 text-xs font-medium text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {hostTrunkMutation.isPending ? 'Adding…' : 'Add'}
                      </button>
                    </div>
                    {/* The agent's own words. "already has a trunk to that address" is a useful
                        answer, not a failure — it means the coverage is already there under
                        another name. */}
                    {hostTrunkMutation.isError && (
                      <p className="mt-1 text-[11px] text-amber-800">
                        {hostTrunkMutation.error?.body?.error
                          || hostTrunkMutation.error?.message
                          || 'The PBX would not create that trunk.'}
                      </p>
                    )}
                  </form>
                )}
              </>
            ) : hostTrunks ? (
              <>
                <p className="mt-1 text-sm text-gray-700">
                  No carrier trunk — this PBX cannot complete a call.
                </p>
                {hostTrunks.can_create ? (
                  <>
                    <button
                      type="button"
                      onClick={() => hostTrunkMutation.mutate({ name: 'Commio', host: 'icr.commio.com' })}
                      disabled={hostTrunkMutation.isPending}
                      className="mt-2 cursor-pointer rounded-md bg-gray-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-900 disabled:cursor-wait disabled:opacity-70"
                    >
                      {hostTrunkMutation.isPending ? 'Creating on the PBX…' : 'Create the Commio trunk'}
                    </button>
                    {/* Stated because it is the surprising part, and it is verified rather than
                        assumed: pbx-01's live Commio trunk carries authentication off, no secret,
                        no username, no registration. The credential half does not exist — the
                        allowlist below IS the authentication. */}
                    <p className="mt-1 text-[11px] text-gray-400">
                      <span className="font-mono">Commio</span> → <span className="font-mono">icr.commio.com</span>,
                      no credentials.
                    </p>
                  </>
                ) : (
                  // NOT a limitation worth a paragraph. Kevin, 2026-09-06: these two hosts ARE
                  // configurable — just in FreePBX, which the operator already knows. One line.
                  <p className="mt-1 text-xs text-gray-500">
                    {hostTrunks.create_refusal?.kind === 'not_applicable'
                      ? 'Add trunks for this host on the PBX itself.'
                      : hostTrunks.create_refusal?.reason || 'This host cannot have a trunk created through Verso.'}
                  </p>
                )}
                {hostTrunkMutation.isError && (
                  <p className="mt-2 text-sm text-red-700">
                    {hostTrunkMutation.error.body?.error || hostTrunkMutation.error.message}
                  </p>
                )}
                {hostTrunkMutation.data?.warning && (
                  <p className="mt-2 text-sm text-amber-800">{hostTrunkMutation.data.warning}</p>
                )}
              </>
            ) : (
              // NOT "no trunks". An agent too old to answer `trunk-read` 409s, and reporting that
              // as an empty host would be the same confusion the capability layer exists to end.
              <p className="mt-1 text-xs text-gray-400">
                This host&apos;s agent cannot report its trunks.
              </p>
            )}
          </div>

          {trunkInfo?.configured && (
          <div className="mt-3 border-t border-gray-100 pt-3">
          <p className="text-xs font-medium text-gray-700">At Commio</p>
          <p className="mt-0.5 text-xs text-gray-500">
            Commio accepts outbound calls only from an address it allowlists.
          </p>

          {trunkInfo.match ? (
            <p className="mt-2 text-sm text-gray-700">
              <span className="font-mono">{trunkInfo.match.name}</span> at{' '}
              <span className="font-mono">{trunkInfo.match.address}</span>{' '}
              <span className="text-gray-500">(matched on name)</span>
            </p>
          ) : (
            <>
              <p className="mt-2 text-sm text-gray-700">No trunk for this host.</p>
              <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
                {(trunkInfo.trunks || []).map((t) => (
                  <li key={t.id}>
                    <span className="font-mono">{t.name}</span> → <span className="font-mono">{t.address}</span>
                    {' '}· profile {t.profile_id} · {t.channel_limit} channels
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="text"
                  value={trunkAddress}
                  onChange={(e) => setTrunkAddress(e.target.value)}
                  placeholder="Public IP this PBX calls out from"
                  className="w-64 rounded-md border border-gray-300 px-2 py-1 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => commioTrunkMutation.mutate(trunkAddress.trim())}
                  disabled={!trunkAddress.trim() || commioTrunkMutation.isPending}
                  className="cursor-pointer rounded-md bg-gray-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {commioTrunkMutation.isPending ? 'Creating in Commio…' : 'Add the outbound trunk'}
                </button>
              </div>
              {/* The profile, trunk type and channel limit are copied from the trunks above rather
                  than chosen here — a different outbound profile would rate this host's calls
                  differently. Worth one line so the operator knows nothing was invented. */}
              <p className="mt-1 text-[11px] text-gray-400">Settings copied from the trunks above.</p>
            </>
          )}
          {commioTrunkMutation.isError && (
            <p className="mt-2 text-sm text-red-700">
              {commioTrunkMutation.error.body?.error || commioTrunkMutation.error.message}
            </p>
          )}
          {commioTrunkMutation.data?.warning && (
            <p className="mt-2 text-sm text-amber-800">{commioTrunkMutation.data.warning}</p>
          )}
          </div>
          )}
        </div>
      )}

      {/* The three connection cards */}
      {/* The host's interfaces, above the three-card summary: which addresses it answers on
          and which GDMS SIP Server each one is. The summary cards below still show the older
          single per-host pin, which is now only the fallback. */}
      <PbxHostInterfaces agentId={agentId} canEdit={isAdmin} />

      <div className="grid gap-4 md:grid-cols-3">
        <Card title="Agent">
          <Field label="Endpoint" value={verify?.freepbx?.endpoint} mono />
          <p className="pt-1 text-[11px] text-gray-400">Cloud → agent management tunnel.</p>
        </Card>

        <Card title="Commio route" verdict={matchVerdict}>
          <Field label="Profile" value={verify?.commio?.profile_name || (verify?.commio?.pinned_route_id ? `#${verify.commio.pinned_route_id}` : null)} />
          <Field label="Delivers to" value={(verify?.commio?.route_to_addresses || []).join(', ')} mono />
          <p className="pt-1 text-[11px] text-gray-400">Carrier routes matching DIDs here.</p>
        </Card>

        <Card title="GDMS SIP server" verdict={matchVerdict}>
          <Field label="Server" value={verify?.gdms?.server_name || (verify?.gdms?.pinned_sip_server_id ? `#${verify.gdms.pinned_sip_server_id}` : null)} />
          <Field label="Address" value={verify?.gdms?.sip_server} mono />
          <p className="pt-1 text-[11px] text-gray-400">Devices register here.</p>
        </Card>
      </div>

      {/* DID routing drift + bulk re-route */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">DID routing</h3>
            <p className="text-xs text-gray-500">
              Assigned DIDs on this host and whether their live Commio routing matches the assigned profile.
            </p>
          </div>
          {isAdmin && offRoute.length > 0 && (
            <button
              onClick={() => { setRerouteResult(null); rerouteMutation.mutate(); }}
              disabled={rerouteMutation.isPending}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              title={`Re-point ${offRoute.length} DID(s) at ${verify?.commio?.profile_name || 'the assigned route'}`}
            >
              {rerouteMutation.isPending ? 'Re-routing…' : `Re-route ${offRoute.length} DID${offRoute.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>

        {verifyLoading ? (
          <p className="mt-3 text-sm text-gray-400">Checking…</p>
        ) : (
          <div className="mt-3 text-sm">
            <div className="flex gap-6">
              <span><span className="font-semibold text-gray-900">{drift?.total ?? 0}</span> assigned</span>
              <span className="text-green-700"><span className="font-semibold">{drift?.on_pinned_route ?? 0}</span> on assigned route</span>
              <span className={offRoute.length ? 'text-amber-700' : 'text-gray-400'}>
                <span className="font-semibold">{offRoute.length}</span> off route
              </span>
            </div>
            {offRoute.length > 0 && (
              <table className="mt-3 w-full text-xs">
                <thead className="text-left text-gray-400">
                  <tr><th className="py-1">DID</th><th className="py-1">Live route</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {offRoute.map((d) => (
                    <tr key={d.e164}>
                      <td className="py-1 font-mono">{d.e164}</td>
                      <td className="py-1 font-mono text-amber-700">{d.route_id ?? 'none'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {rerouteResult && (
              <p className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                Re-routed {rerouteResult.rerouted?.length ?? 0} DID(s) to route {rerouteResult.route_id}
                {rerouteResult.skipped?.length ? `, skipped ${rerouteResult.skipped.length} already on route` : ''}.
              </p>
            )}
            {rerouteMutation.isError && (
              <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {rerouteMutation.error?.body?.error || rerouteMutation.error?.message || 'Re-route failed'}
              </p>
            )}
          </div>
        )}
      </div>

      </>)}

      {editing && (
        <PbxHostEditModal
          agent={host}
          onCancel={() => { setEditing(false); editMutation.reset(); }}
          onSave={(body) => {
            if (!body || Object.keys(body).length === 0) { setEditing(false); return; }
            editMutation.mutate(body);
          }}
          isPending={editMutation.isPending}
          error={editMutation.isError ? (editMutation.error?.body?.error || editMutation.error?.message || 'Save failed') : null}
        />
      )}
    </div>
  );
}
